import React, { useMemo, useState } from 'react';
import {
    useReactTable,
    getCoreRowModel,
    flexRender,
    createColumnHelper,
} from '@tanstack/react-table';
import { useQuery } from '@tanstack/react-query';
import { fetchProducts } from '../../services/productFeed';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../ui/table";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Select } from "../ui/select";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../ui/dialog";
import { Loader2, Download, LayoutTemplate, Package, RefreshCw, FileCode, Check, Copy } from "lucide-react";
import BatchTemplateModal from './BatchTemplateModal';
import PageHeader from '../PageHeader/PageHeader';
import { generateFeed } from '../../services/productFeed';
import queryClient from '../../lib/queryClient';
import { cn } from "../../lib/utils";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const columnHelper = createColumnHelper();

const ProductTable = () => {
    // Pagination state
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 20,
    });

    // Multi-select state
    const [selectedProducts, setSelectedProducts] = useState(new Set());
    const [showBatchModal, setShowBatchModal] = useState(false);

    // SyncBar state
    const [syncing, setSyncing] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [progress, setProgress] = useState(0);
    const [total, setTotal] = useState(0);
    const [message, setMessage] = useState("");
    const [completed, setCompleted] = useState(false);

    // Feed Modal state
    const [showFeedModal, setShowFeedModal] = useState(false);
    const [feedUrl, setFeedUrl] = useState("");
    const [copied, setCopied] = useState(false);

    const startSync = () => {
        setSyncing(true);
        setProgress(0);
        setTotal(0);
        setMessage("Starting sync...");
        setCompleted(false);

        const eventSource = new EventSource(`${BASE_URL}/sync/shopify/stream`);

        eventSource.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);

                switch (data.type) {
                    case 'progress':
                        setMessage(data.message);
                        if (data.count) setProgress(data.count);
                        if (data.total) setTotal(data.total);
                        break;
                    case 'product':
                        setMessage(`Saved: ${data.product}`);
                        setProgress(prev => Math.max(prev, data.count));
                        break;
                    case 'error':
                        console.error("Sync error:", data.message);
                        setMessage(`Error: ${data.message}`);
                        break;
                    case 'complete':
                        setMessage(data.message);
                        setCompleted(true);
                        setSyncing(false);
                        eventSource.close();
                        queryClient.invalidateQueries({ queryKey: ['products'] });

                        setTimeout(() => {
                            setMessage("");
                            setCompleted(false);
                        }, 3000);
                        break;
                    default:
                        break;
                }
            } catch (e) {
                console.error("Error parsing SSE data", e);
            }
        };

        eventSource.onerror = (error) => {
            console.error('SSE connection error:', error);
            setMessage("Connection error");
            setSyncing(false);
            eventSource.close();
        };
    };

    const handleGenerateFeed = async () => {
        setGenerating(true);
        setMessage("Generating XML feed...");
        try {
            const result = await generateFeed();
            console.log("Generate Feed Result:", result);

            setMessage("Feed generated successfully!");

            const fullUrl = result.url || result.file_path || "";
            setFeedUrl(fullUrl);
            setShowFeedModal(true);

            setTimeout(() => {
                setMessage("");
            }, 3000);
        } catch (error) {
            setMessage(`Error generating feed: ${error.message}`);
        } finally {
            setGenerating(false);
        }
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(feedUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const { data, isLoading, isError, error } = useQuery({
        queryKey: ['products', pagination.pageIndex + 1, pagination.pageSize],
        queryFn: () => fetchProducts(pagination.pageIndex + 1, pagination.pageSize),
        keepPreviousData: true, // Keep data while fetching new page
    });

    const tableData = useMemo(() => data?.products || [], [data]);
    const totalCount = data?.total || 0;

    // Toggle product selection
    const toggleProductSelection = (productId) => {
        setSelectedProducts(prev => {
            const newSet = new Set(prev);
            if (newSet.has(productId)) {
                newSet.delete(productId);
            } else {
                newSet.add(productId);
            }
            return newSet;
        });
    };

    // Toggle select all
    const toggleSelectAll = () => {
        if (selectedProducts.size === tableData.length) {
            setSelectedProducts(new Set());
        } else {
            setSelectedProducts(new Set(tableData.map(p => p.id)));
        }
    };

    // Get selected products data
    const getSelectedProductsData = () => {
        return tableData.filter(p => selectedProducts.has(p.id));
    };

    // Define columns
    const columns = useMemo(() => [
        columnHelper.display({
            id: 'select',
            header: () => (
                <input
                    type="checkbox"
                    checked={selectedProducts.size === tableData.length && tableData.length > 0}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 text-[#8E0DFF] bg-gray-100 border-gray-300 rounded focus:ring-[#8E0DFF]"
                />
            ),
            cell: ({ row }) => (
                <input
                    type="checkbox"
                    checked={selectedProducts.has(row.original.id)}
                    onChange={() => toggleProductSelection(row.original.id)}
                    className="w-4 h-4 text-[#8E0DFF] bg-gray-100 border-gray-300 rounded focus:ring-[#8E0DFF]"
                />
            ),
        }),
        columnHelper.accessor('images', {
            header: 'Image',
            cell: info => {
                const images = info.getValue();
                const firstImage = images && images.length > 0 ? images[0].src : null;
                return firstImage ? (
                    <img src={firstImage} alt="Product" className="h-10 w-10 object-cover rounded-md" />
                ) : (
                    <span className="text-gray-400 text-sm italic">No Image</span>
                );
            },
        }),
        columnHelper.accessor('title', {
            header: 'Title',
            cell: info => <span className="font-medium text-left">{info.getValue()}</span>,
        }),
        columnHelper.accessor('status', {
            header: 'Status',
            cell: info => {
                const status = info.getValue();
                let variant = "default";
                if (status === 'archived') variant = "destructive";
                if (status === 'draft') variant = "secondary";

                return (
                    <div className="text-left">
                        <Badge variant={variant} className="capitalize">
                            {status}
                        </Badge>
                    </div>
                );
            },
        }),
        columnHelper.accessor('vendor', {
            header: 'Vendor',
            cell: info => <div className="text-left">{info.getValue()}</div>,
        }),
        columnHelper.accessor('min_price', {
            header: 'Price',
            cell: info => {
                const price = info.getValue();
                const currency = info.row.original.currency || 'INR';
                return <div className="text-left">{new Intl.NumberFormat('en-IN', { style: 'currency', currency: currency }).format(price)}</div>;
            },
        }),
    ], [selectedProducts, tableData.length, toggleSelectAll]);

    const table = useReactTable({
        data: tableData,
        columns,
        pageCount: Math.ceil(totalCount / pagination.pageSize),
        state: {
            pagination,
        },
        onPaginationChange: setPagination,
        manualPagination: true,
        getCoreRowModel: getCoreRowModel(),
    });

    return (
        <div className="h-full flex flex-col">
            <PageHeader
                title="Product Feed"
                description="Manage your products and sync status."
                icon={Package}
            >
                <div className="flex items-center gap-2">
                    {(syncing || generating || message) ? (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground mr-4 animate-in fade-in slide-in-from-left-5">
                            {(syncing || generating) && <Loader2 className="h-4 w-4 animate-spin" />}
                            <span>{message}</span>
                            {syncing && <span className="font-mono text-xs">({progress} processed)</span>}
                        </div>
                    ) : (
                        <div className="text-sm text-muted-foreground mr-4">
                            Total Products: {totalCount}
                        </div>
                    )}

                    {selectedProducts.size > 0 && (
                        <div className="flex items-center gap-2 border-r pr-2 mr-2 border-gray-200">
                            <span className="text-sm text-muted-foreground mr-2">
                                {selectedProducts.size} selected
                            </span>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setShowBatchModal(true)}
                                className="flex items-center gap-2"
                            >
                                <LayoutTemplate className="w-4 h-4" />
                                Apply Template
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setShowBatchModal(true)}
                                className="flex items-center gap-2"
                            >
                                <Download className="w-4 h-4" />
                                Apply Template & Download
                            </Button>
                        </div>
                    )}
                    
                    <Button
                        onClick={handleGenerateFeed}
                        disabled={generating || syncing}
                        variant="outline"
                        size="sm"
                    >
                        {generating ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Generating...
                            </>
                        ) : (
                            <>
                                <FileCode className="mr-2 h-4 w-4" />
                                Generate Feed
                            </>
                        )}
                    </Button>

                    <Button
                        onClick={startSync}
                        disabled={syncing || generating}
                        variant={completed ? "outline" : "default"}
                        size="sm"
                        className={cn(completed && "border-green-500 text-green-600 hover:text-green-700 hover:bg-green-50")}
                    >
                        {syncing ? (
                            <>
                                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                                Syncing...
                            </>
                        ) : completed ? (
                            "Sync Complete"
                        ) : (
                            "Sync from Shopify"
                        )}
                    </Button>
                </div>
            </PageHeader>
            
            {/* Progress Bar (Simple) */}
            {syncing && (
                <div className="h-1 w-full bg-secondary overflow-hidden shrink-0">
                    <div
                        className="h-full bg-primary transition-all duration-300 ease-in-out"
                        style={{ width: `${Math.min((progress / (total || 100)) * 100, 100)}%` }} // Rough estimate
                    />
                    <div className="h-full w-full bg-primary origin-left animate-progress-indeterminate opacity-20" />
                </div>
            )}

            <div className="container mx-auto py-10 flex-1 overflow-auto">

            {isError && <div className="bg-destructive/15 text-destructive p-3 rounded-md mb-4">Error: {error.message}</div>}

            <div className="rounded-md border bg-card text-card-foreground shadow-sm">
                <Table>
                    <TableHeader className="bg-muted/30">
                        {table.getHeaderGroups().map(headerGroup => (
                            <TableRow key={headerGroup.id} className="hover:bg-muted/30 border-b border-border">
                                {headerGroup.headers.map(header => (
                                    <TableHead key={header.id} className="h-12 px-4 text-left align-middle font-medium text-muted-foreground">
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(
                                                header.column.columnDef.header,
                                                header.getContext()
                                            )}
                                    </TableHead>
                                ))}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell colSpan={columns.length} className="h-24 text-center">
                                    <div className="flex justify-center items-center gap-2 text-muted-foreground">
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        Loading products...
                                    </div>
                                </TableCell>
                            </TableRow>
                        ) : table.getRowModel().rows.length > 0 ? (
                            table.getRowModel().rows.map(row => (
                                <TableRow key={row.id} className="transition-colors hover:bg-muted/30 data-[state=selected]:bg-muted">
                                    {row.getVisibleCells().map(cell => (
                                        <TableCell key={cell.id} className="p-4 align-middle text-left">
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={columns.length} className="h-24 text-center text-muted-foreground">
                                    No products found.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>

            <div className="flex items-center justify-end space-x-2 py-4">
                <div className="flex-1 text-sm text-muted-foreground">
                    Page {table.getState().pagination.pageIndex + 1} of {Math.max(1, table.getPageCount())}
                </div>
                <div className="flex items-center space-x-2">
                    <Select
                        className="w-[100px]"
                        value={table.getState().pagination.pageSize}
                        onChange={e => {
                            table.setPageSize(Number(e.target.value));
                        }}
                    >
                        {[10, 20, 30, 40, 50].map(pageSize => (
                            <option key={pageSize} value={pageSize}>
                                Show {pageSize}
                            </option>
                        ))}
                    </Select>

                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => table.previousPage()}
                        disabled={!table.getCanPreviousPage()}
                    >
                        Previous
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => table.nextPage()}
                        disabled={!table.getCanNextPage()}
                    >
                        Next
                    </Button>
                </div>
            </div>

            {/* Batch Template Modal */}
            {showBatchModal && (
                <BatchTemplateModal
                    selectedProducts={getSelectedProductsData()}
                    onClose={() => setShowBatchModal(false)}
                    onComplete={() => {
                        setShowBatchModal(false);
                        setSelectedProducts(new Set());
                    }}
                />
            )}

            {/* Feed URL Modal */}
            <Dialog open={showFeedModal} onOpenChange={setShowFeedModal}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Feed Generated Successfully</DialogTitle>
                        <DialogDescription>
                            Your XML product feed is ready. You can access it at the following URL:
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex items-center space-x-2">
                        <div className="grid flex-1 gap-2">
                            <input
                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                value={feedUrl}
                                readOnly
                            />
                        </div>
                        <Button type="button" size="sm" className="px-3" onClick={copyToClipboard}>
                            <span className="sr-only">Copy</span>
                            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                        </Button>
                    </div>
                    <DialogFooter className="sm:justify-start">
                        <Button type="button" variant="secondary" onClick={() => setShowFeedModal(false)}>
                            Close
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
        </div>
    );
};

export default ProductTable;
